import { useEffect } from 'react';
import { useAuth } from '../../auth/hooks/useAuth';
import { useIngresosStore } from '../../../shared/store/ingresos.store';
import type { Ingreso } from '../../../shared/types';

// Importar el hook de Firebase existente
import { useIngresos } from './useIngresos';

export const useIngresosOptimistic = () => {
  const { user } = useAuth();
  const {
    ingresos: cachedIngresos,
    isLoading: cacheLoading,
    error: cacheError,
    addIngresoOptimistic,
    removeIngresoOptimistic,
    setIngresos,
    setError,
    clearError
  } = useIngresosStore();

  // Hook de Firebase para sincronización
  const { 
    ingresos: firebaseIngresos, 
    loading: firebaseLoading,
    error: firebaseError,
    createIngreso: createFirebaseIngreso,
    deleteIngreso: deleteFirebaseIngreso
  } = useIngresos();

  // Sincronizar datos de Firebase con cache local al inicializar
  useEffect(() => {
    if (firebaseIngresos && Array.isArray(firebaseIngresos)) {
      // Filtrar solo ingresos reales de Firebase (no temporales)
      const realFirebaseIngresos = firebaseIngresos.filter(ing => !ing.id.startsWith('temp_'));
      
      // Obtener ingresos temporales del cache
      const tempIngresos = cachedIngresos.filter(ing => ing.id.startsWith('temp_'));
      
      // Debug temporal
      console.log('🔄 SYNC DEBUG:', {
        firebaseCount: realFirebaseIngresos.length,
        tempCount: tempIngresos.length,
        cacheCount: cachedIngresos.length,
        firebaseIds: realFirebaseIngresos.map(i => i.id),
        tempIds: tempIngresos.map(i => i.id)
      });
      
      // Combinar: ingresos de Firebase + ingresos temporales
      const combinedIngresos = [...realFirebaseIngresos, ...tempIngresos];
      
      // Solo actualizar si la combinación es diferente al cache actual
      if (JSON.stringify(combinedIngresos.map(i => i.id).sort()) !== 
          JSON.stringify(cachedIngresos.map(i => i.id).sort())) {
        console.log('📝 Actualizando cache con:', combinedIngresos.length, 'ingresos');
        setIngresos(combinedIngresos);
      }
    }
  }, [firebaseIngresos, setIngresos, cachedIngresos]);

  // Manejar errores de Firebase
  useEffect(() => {
    if (firebaseError) {
      setError(firebaseError);
    }
  }, [firebaseError, setError]);

  // Función para crear ingreso con actualización optimista
  const createIngreso = async (ingresoData: Omit<Ingreso, 'id' | 'userId' | 'createdAt' | 'updatedAt'>) => {
    if (!user) {
      const error = 'Usuario no autenticado';
      setError(error);
      throw new Error(error);
    }

    clearError();
    
    // Datos completos para el ingreso
    const completeIngresoData = {
      ...ingresoData,
      userId: user.uid,
      fecha: ingresoData.fecha || new Date(),
    };

    // 1. Agregar inmediatamente al cache local (OPTIMISTIC UPDATE)
    const tempIngreso = addIngresoOptimistic(completeIngresoData);

    try {
      // 2. Guardar en Firebase en segundo plano
      const realIngresoId = await createFirebaseIngreso({
        concepto: completeIngresoData.concepto,
        monto: completeIngresoData.monto,
        fecha: completeIngresoData.fecha,
        categoria: completeIngresoData.categoria,
        descripcion: completeIngresoData.descripcion || '',
      });
      
      // 3. Cuando Firebase confirma, remover el temporal inmediatamente
      // El useEffect se encargará de agregar la versión real de Firebase
      if (realIngresoId) {
        removeIngresoOptimistic(tempIngreso.id);
      }
      
      return realIngresoId || tempIngreso.id;
    } catch (error) {
      
      // Si falla Firebase, remover del cache local
      removeIngresoOptimistic(tempIngreso.id);
      
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
      setError(`Error guardando ingreso: ${errorMessage}`);
      throw error;
    }
  };

  // Función para eliminar con actualización optimista
  const deleteIngreso = async (id: string) => {
    // 1. Remover inmediatamente del cache local
    removeIngresoOptimistic(id);

    try {
      // 2. Eliminar de Firebase en segundo plano (solo si no es temporal)
      if (!id.startsWith('temp_')) {
        await deleteFirebaseIngreso(id);
      }
    } catch {
      setError('Error eliminando ingreso de Firebase');
      // En caso de error, podríamos restaurar el ingreso al cache
      // Pero por simplicidad, mantenemos la eliminación optimista
    }
  };

  // Combinar loading states
  const isLoading = cacheLoading || firebaseLoading;
  const error = cacheError || firebaseError;

  return {
    ingresos: cachedIngresos,
    isLoading,
    error,
    createIngreso,
    deleteIngreso,
    clearError,
  };
};